import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUrlParams } from '../../hooks/useUrlParams';
import { useTasks } from '../../hooks/useTasks';
import {
    formatHistoryEntry as formatHistoryEntryBase,
    canEditTaskDates as canEditTaskDatesBase,
    canChangeStatus as canChangeStatusBase,
    handleKanbanDragStart as handleKanbanDragStartBase,
    handleKanbanDrop as handleKanbanDropBase
} from '../../utils/tasks';
import { getDefaultWorkspaceId as getDefaultWorkspaceIdBase, closeTaskDetail as closeTaskDetailBase } from '../../utils/tasks-ui';
import { startPanelResize } from '../../utils/resize';
import {
    addTaskTags as addTaskTagsBase,
    handleTagsBlur as handleTagsBlurBase,
    handleTagsKeyDown as handleTagsKeyDownBase,
    addEditTags as addEditTagsBase,
    handleEditTagsBlur as handleEditTagsBlurBase,
    handleEditTagsKeyDown as handleEditTagsKeyDownBase,
    openTaskEdit as openTaskEditBase,
    closeTaskEdit as closeTaskEditBase,
    toggleEditList as toggleEditListBase
} from '../../utils/tasks-edit';
import { toggleTaskList as toggleTaskListBase } from '../../utils/tasks-form';
import {
    handleCreateTask as handleCreateTaskBase,
    loadTaskHistory as loadTaskHistoryBase,
    openTaskDetail as openTaskDetailBase,
    handleAddTaskComment as handleAddTaskCommentBase,
    handleDeleteTask as handleDeleteTaskBase,
    handleStatusChange as handleStatusChangeBase,
    handleExecuteAiTask as handleExecuteAiTaskBase,
    handleExecuteAiTasks as handleExecuteAiTasksBase,
    handleUpdateTaskFields as handleUpdateTaskFieldsBase,
    handleUpdateTask as handleUpdateTaskBase
} from '../../services/tasks';

const initialTaskForm = {
    title: '',
    description: '',
    start_at: '',
    due_at: '',
    notify: false,
    tags: [],
    workspace_id: '',
    assignee_id: '',
    assignee_type: 'user',
    ai_agent_slug: '',
    co_assignee_ids: [],
    watcher_ids: [],
    cabinet_ids: [],
    result_scope: 'assortment',
    result_cabinet_id: '',
    result_skus: [],
    result_metrics: [],
    deal_id: ''
};

const initialTaskEditForm = {
    created_by_id: '',
    title: '',
    description: '',
    start_at: '',
    due_at: '',
    notify: false,
    tags: [],
    workspace_id: '',
    assignee_id: '',
    assignee_type: 'user',
    ai_agent_slug: '',
    co_assignee_ids: [],
    watcher_ids: [],
    cabinet_ids: []
};

const TEXT = {
    confirm_delete: 'Удалить задачу?'
};

export function useTasksController({
    request,
    showStatus,
    setStatus,
    user,
    companyId,
    activeSection,
    activeProjectId,
    workspaces,
    setActiveSection,
    formatDateTime,
    getUserLabel
}) {
    const {
        tasks,
        tasksBusy,
        setTasksBusy,
        tasksView,
        setTasksView,
        kanbanGroup,
        setKanbanGroup,
        taskFilters,
        setTaskFilters,
        taskTags,
        filteredTasks,
        sortedTasks,
        kanbanColumns,
        loadTasks,
        setPresetFilters,
        taskStatusLabels
    } = useTasks({
        request,
        showStatus,
        setStatus,
        user,
        companyId,
        activeSection,
        activeProjectId
    });

    const [taskForm, setTaskForm] = useState(initialTaskForm);
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskTagsInput, setTaskTagsInput] = useState('');
    const [taskEditOpen, setTaskEditOpen] = useState(false);
    const [taskEditForm, setTaskEditForm] = useState(initialTaskEditForm);
    const [taskEditTagsInput, setTaskEditTagsInput] = useState('');
    const [taskEditBusy, setTaskEditBusy] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [taskDetail, setTaskDetail] = useState(null);
    const [taskComments, setTaskComments] = useState([]);
    const [taskCommentBody, setTaskCommentBody] = useState('');
    const [taskCommentFiles, setTaskCommentFiles] = useState([]);
    const [taskDetailBusy, setTaskDetailBusy] = useState(false);
    const [taskDescriptionExpanded, setTaskDescriptionExpanded] = useState(false);
    const [taskHistory, setTaskHistory] = useState([]);
    const [taskHistoryExpanded, setTaskHistoryExpanded] = useState(false);

    // Новая задача по умолчанию берёт проект из явного фильтра "Проект" в разделе задач,
    // а не из глобального activeProjectId — тот выставляется автоматически в первый проект
    // компании для других разделов и не отражает осознанный выбор пользователя здесь.
    useEffect(() => {
        const defaultWorkspaceId = taskFilters.workspaceId || activeProjectId;
        if (!defaultWorkspaceId) return;
        setTaskForm((prev) => (prev.workspace_id ? prev : { ...prev, workspace_id: defaultWorkspaceId }));
    }, [taskFilters.workspaceId, activeProjectId]);

    const taskDetailPanelRef = useRef(null);
    const resizeState = useRef(null);
    const [taskDetailSize, setTaskDetailSize] = useState(() => {
        if (typeof window === 'undefined') return null;
        const raw = localStorage.getItem('taskDetailSize');
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    });

    const taskEditPanelRef = useRef(null);
    const editResizeState = useRef(null);
    const [taskEditSize, setTaskEditSize] = useState(() => {
        if (typeof window === 'undefined') return null;
        const raw = localStorage.getItem('taskEditSize');
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    });

    const taskCreatePanelRef = useRef(null);
    const createResizeState = useRef(null);
    const [taskCreateSize, setTaskCreateSize] = useState(() => {
        if (typeof window === 'undefined') return null;
        const raw = localStorage.getItem('taskCreateSize');
        if (!raw) return null;
        try {
            return JSON.parse(raw);
        } catch (error) {
            return null;
        }
    });

    const getDefaultWorkspaceId = () => getDefaultWorkspaceIdBase(taskForm, workspaces);

    const handleCreateTask = (event) =>
        handleCreateTaskBase({
            event,
            companyId,
            taskForm,
            getDefaultWorkspaceId,
            request,
            setTasksBusy,
            setStatus,
            setTaskForm,
            setTaskTagsInput,
            setTaskModalOpen,
            loadTasks,
            showStatus
        });

    const handleCreateTaskForDeal = (deal) => {
        if (!deal) return;
        setTaskForm((prev) => ({
            ...prev,
            deal_id: deal.id,
            title: prev.title || `По сделке: ${deal.title || ''}`.trim(),
        }));
        setActiveSection('tasks');
        setTaskModalOpen(true);
    };

    const handleOpenTasksForDeal = (deal) => {
        if (!deal) return;
        setTaskFilters((prev) => ({ ...prev, dealId: deal.id }));
        setActiveSection('tasks');
        setTasksView('list');
    };

    const loadTaskHistory = (taskId) =>
        loadTaskHistoryBase({
            taskId,
            request,
            setTaskHistory
        });

    const openTaskDetail = (taskId) =>
        openTaskDetailBase({
            taskId,
            request,
            setSelectedTask,
            setTaskDetailBusy,
            setTaskDescriptionExpanded,
            setTaskHistoryExpanded,
            setStatus,
            setTaskDetail,
            setTaskComments,
            setTaskHistory,
            showStatus
        });

    // Ссылка из уведомления (?task=<id>) открывает карточку этой задачи и сразу убирает параметр из адреса.
    const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
    const urlTaskId = getUrlParam('task');
    useEffect(() => {
        if (!urlTaskId) return;
        openTaskDetail(urlTaskId);
        updateUrlParams({ task: '' });
        // eslint-disable-next-line
    }, [urlTaskId]);

    const refreshOpenTaskDetail = useCallback(async () => {
        if (!selectedTask) return;
        try {
            const [detail, comments, history] = await Promise.all([
                request(`/api/v1/tasks/${selectedTask}`, {}),
                request(`/api/v1/tasks/${selectedTask}/comments`, {}),
                request(`/api/v1/tasks/${selectedTask}/history`, {}),
            ]);
            setTaskDetail(detail);
            setTaskComments(comments || []);
            setTaskHistory(history || []);
        } catch (error) {
            // Background refresh should not interrupt the user while editing/commenting.
            console.warn('Failed to refresh task detail:', error);
        }
    }, [request, selectedTask]);

    useEffect(() => {
        if (!selectedTask || !taskDetail) return undefined;
        const aiBusy = taskDetail.queue_status === 'queued' || taskDetail.queue_status === 'processing';
        const intervalMs = aiBusy ? 2500 : 10000;
        const intervalId = window.setInterval(() => {
            refreshOpenTaskDetail();
        }, intervalMs);
        return () => window.clearInterval(intervalId);
    }, [refreshOpenTaskDetail, selectedTask, taskDetail]);

    const closeTaskDetail = () =>
        closeTaskDetailBase({
            setSelectedTask,
            setTaskDetail,
            setTaskComments,
            setTaskCommentBody,
            setTaskCommentFiles,
            setTaskEditOpen,
            setTaskDescriptionExpanded,
            setTaskHistoryExpanded,
            setTaskHistory
        });

    const startTaskResize = (event) =>
        startPanelResize({
            event,
            panelRef: taskDetailPanelRef,
            sizeKey: 'taskDetailSize',
            sizeRef: resizeState,
            setSize: setTaskDetailSize,
            minWidth: 980,
            minHeight: 620
        });

    const startTaskEditResize = (event) =>
        startPanelResize({
            event,
            panelRef: taskEditPanelRef,
            sizeKey: 'taskEditSize',
            sizeRef: editResizeState,
            setSize: setTaskEditSize,
            minWidth: 860,
            minHeight: 620
        });

    const startTaskCreateResize = (event) =>
        startPanelResize({
            event,
            panelRef: taskCreatePanelRef,
            sizeKey: 'taskCreateSize',
            sizeRef: createResizeState,
            setSize: setTaskCreateSize,
            minWidth: 860,
            minHeight: 560,
        });

    const openTaskEdit = () =>
        openTaskEditBase(taskDetail, setTaskEditForm, setTaskEditTagsInput, setTaskEditOpen);

    const closeTaskEdit = () => closeTaskEditBase(setTaskEditOpen);

    const toggleEditList = (key, value) =>
        toggleEditListBase(key, value, setTaskEditForm);

    const handleAddTaskComment = (event) =>
        handleAddTaskCommentBase({
            event,
            selectedTask,
            taskDetail,
            taskCommentBody,
            taskCommentFiles,
            request,
            setTaskDetailBusy,
            setStatus,
            setTaskComments,
            setTaskHistory,
            setTaskCommentBody,
            setTaskCommentFiles,
            showStatus
        });

    const handleDeleteTask = () =>
        handleDeleteTaskBase({
            taskDetail,
            confirmed:
                typeof window !== 'undefined'
                    ? window.confirm(taskDetail?.title ? `${TEXT.confirm_delete.replace('?', '')} «${taskDetail.title}»?` : TEXT.confirm_delete)
                    : false,
            request,
            setTaskDetailBusy,
            setStatus,
            loadTasks,
            closeTaskDetail,
            showStatus
        });

    const handleStatusChange = (taskId, status) =>
        handleStatusChangeBase({
            taskId,
            status,
            request,
            setTaskDetailBusy,
            setStatus,
            setTaskDetail,
            loadTaskHistory,
            loadTasks,
            showStatus
        });

    const handleExecuteAiTask = (taskId) =>
        handleExecuteAiTaskBase({
            taskId,
            request,
            setTaskDetailBusy,
            setStatus,
            setTaskDetail,
            loadTaskHistory,
            loadTasks,
            showStatus
        });

    const handleExecuteAiTasks = () =>
        handleExecuteAiTasksBase({
            companyId,
            workspaceId: activeProjectId,
            request,
            setTasksBusy,
            setStatus,
            loadTasks,
            showStatus
        });

    const handleUpdateTaskFields = (taskId, fields) =>
        handleUpdateTaskFieldsBase({
            taskId,
            fields,
            taskDetail,
            request,
            setTaskDetail,
            setTaskDetailBusy,
            setStatus,
            loadTasks,
            showStatus
        });

    const handleUpdateTask = (event) =>
        handleUpdateTaskBase({
            event,
            taskDetail,
            taskEditForm,
            request,
            setTaskEditBusy,
            setStatus,
            setTaskDetail,
            setTaskEditOpen,
            setTaskEditForm,
            setTaskEditTagsInput,
            loadTaskHistory,
            loadTasks,
            showStatus
        });

    const addTaskTags = (value) => addTaskTagsBase(value, setTaskForm);

    const handleTagsBlur = () =>
        handleTagsBlurBase(taskTagsInput, setTaskTagsInput, addTaskTags);

    const handleTagsKeyDown = (event) =>
        handleTagsKeyDownBase(event, taskTagsInput, setTaskTagsInput, addTaskTags);

    const addEditTags = (value) => addEditTagsBase(value, setTaskEditForm);

    const handleEditTagsBlur = () =>
        handleEditTagsBlurBase(taskEditTagsInput, setTaskEditTagsInput, addEditTags);

    const handleEditTagsKeyDown = (event) =>
        handleEditTagsKeyDownBase(event, taskEditTagsInput, setTaskEditTagsInput, addEditTags);

    const toggleTaskList = (key, value) =>
        toggleTaskListBase(key, value, setTaskForm);

    const canEditTaskDates = (task) => canEditTaskDatesBase(task, user);

    const canChangeStatus = (task, nextStatus) => canChangeStatusBase(task, nextStatus, user);

    const handleKanbanDragStart = (event, taskId) =>
        handleKanbanDragStartBase(event, taskId);

    const handleKanbanDrop = (event, status) =>
        handleKanbanDropBase({
            event,
            columnKey: status,
            filteredTasks,
            kanbanGroup,
            canChangeStatus,
            canEditTaskDates,
            handleStatusChange,
            handleUpdateTaskFields,
            showStatus
        });

    const formatHistoryEntry = (entry) =>
        formatHistoryEntryBase(entry, formatDateTime, getUserLabel);

    return {
        tasks,
        tasksBusy,
        tasksView,
        setTasksView,
        kanbanGroup,
        setKanbanGroup,
        taskFilters,
        setTaskFilters,
        taskTags,
        filteredTasks,
        sortedTasks,
        kanbanColumns,
        loadTasks,
        setPresetFilters,
        taskStatusLabels,
        taskForm,
        setTaskForm,
        taskModalOpen,
        setTaskModalOpen,
        taskTagsInput,
        setTaskTagsInput,
        taskEditOpen,
        setTaskEditOpen,
        taskEditForm,
        setTaskEditForm,
        taskEditTagsInput,
        setTaskEditTagsInput,
        taskEditBusy,
        setTaskEditBusy,
        selectedTask,
        taskDetail,
        taskDetailBusy,
        taskDetailPanelRef,
        taskDetailSize,
        taskDescriptionExpanded,
        setTaskDescriptionExpanded,
        taskHistory,
        taskHistoryExpanded,
        setTaskHistoryExpanded,
        taskComments,
        taskCommentBody,
        taskCommentFiles,
        setTaskCommentBody,
        setTaskCommentFiles,
        taskEditPanelRef,
        taskEditSize,
        taskCreatePanelRef,
        taskCreateSize,
        startTaskCreateResize,
        handleCreateTask,
        handleCreateTaskForDeal,
        handleOpenTasksForDeal,
        loadTaskHistory,
        openTaskDetail,
        closeTaskDetail,
        startTaskResize,
        startTaskEditResize,
        openTaskEdit,
        closeTaskEdit,
        toggleEditList,
        handleAddTaskComment,
        handleDeleteTask,
        handleStatusChange,
        handleExecuteAiTask,
        handleExecuteAiTasks,
        handleUpdateTaskFields,
        handleUpdateTask,
        addTaskTags,
        handleTagsBlur,
        handleTagsKeyDown,
        addEditTags,
        handleEditTagsBlur,
        handleEditTagsKeyDown,
        toggleTaskList,
        canEditTaskDates,
        canChangeStatus,
        handleKanbanDragStart,
        handleKanbanDrop,
        formatHistoryEntry
    };
}
